import Component from '@glimmer/component'
import { get, action } from '@ember/object'
import { guidFor } from '@ember/object/internals'
import { service } from '@ember/service'
import { groupBy } from 'lodash'
import { tracked } from '@glimmer/tracking'
import { GradeSelect } from 'teacher-ui-client/components/base/select/grade-position'
import { AllGradesOption } from 'teacher-ui-client/components/base/select/grade-position/options'
import { t } from 'ember-intl'
import { on } from '@ember/modifier'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { eq, or } from 'ember-truth-helpers'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { concat } from '@ember/helper'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { array } from '@ember/helper'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { trackedFunction } from 'reactiveweb/function'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type { AssignmentOption } from 'teacher-ui-client/components/assignments-admin/assignment-form'
import { assert } from '@ember/debug'

interface StageAssignmentOption extends AssignmentOption {
  stage: string
}

interface Signature {
  Args: {
    precinct: Precinct
    selectAssignmentAction: (option?: AssignmentOption) => void
    selectedAssignmentOption?: AssignmentOption
  }
}

function lessonNumberToStage(lessonNum: string | number = NaN) {
  const lessonNumber = typeof lessonNum === 'string' ? Number.parseInt(lessonNum, 10) : lessonNum
  switch (true) {
    case lessonNumber >= 1 && lessonNumber < 10:
      return 'begin'
    case lessonNumber < 19:
      return 'early'
    case lessonNumber < 28:
      return 'mid'
    default:
      return 'late'
  }
}

export class ActivitySelectorLessonsByStageList extends Component<Signature> {
  @service subscriptionType!: SubscriptionTypeService

  @service lessonInfo!: LessonInfoService

  @service gradeSets!: GradeSetsService

  loadOptions = trackedFunction(this, async () => {
    assert('unexpected precinct for lesson-by-stage-list', this.args.precinct === Precinct.REX_SPELLING)

    await this.lessonInfo.loadLessonInfoFor([this.args.precinct])
    return this.lessonInfo.lessonsFor(this.args.precinct).map<StageAssignmentOption>((data) => {
      const grade = typeof data.grade_position === 'number' ? this.gradeSets.findByPosition(data.grade_position) : null
      return {
        id: String(data.id),
        label: `${grade?.name ?? ''}.${data.grade_lesson_number} - ${data.name}`,
        gradePosition: data.grade_position,
        assignmentData: {
          assignment_id: String(data.lesson_number),
          assignment_type: 'lesson',
          grade_position: data.grade_position,
          grade_lesson_number: data.grade_lesson_number,
        },
        stage: lessonNumberToStage(data.lesson_number),
      }
    })
  })

  get options() {
    return this.loadOptions.value ?? []
  }

  get componentId() {
    return guidFor(this)
  }

  @tracked currentGradePosition: number = AllGradesOption.id

  get gradePositions() {
    const gradePositions = this.options.map((option) => option.gradePosition)

    const definedGradePositions = gradePositions.filter((pos): pos is number => typeof pos === 'number')
    const uniqGradePositions = [...new Set(definedGradePositions)]
    return uniqGradePositions.sort((a, b) => a - b)
  }

  get filteredOptions() {
    return this.options.filter(
      (option) =>
        this.currentGradePosition === AllGradesOption.id || option.gradePosition === this.currentGradePosition,
    )
  }

  get optionsGroupedByStage() {
    return groupBy(this.filteredOptions, 'stage')
  }

  get stageGroupKeys() {
    return Object.keys(this.optionsGroupedByStage)
  }

  @action
  selectGradePosition(gradePosition: number) {
    this.currentGradePosition = gradePosition
  }

  @action
  selectOption(optionId: string) {
    const foundOption = this.options.find((option) => option.id === optionId)
    this.args.selectAssignmentAction(foundOption)
  }

  <template>
    <div class="flex justify-between md:flex-wrap">
      <div class="mb-4 px-3 md:w-full lg:w-1/2">
        <label id="{{this.componentId}}-assignment-grade-label" class="mb-1">
          {{t "grade.label"}}
        </label>
        <GradeSelect
          class="w-full"
          name="activity-grade"
          aria-labelledby="{{this.componentId}}-assignment-grade-label"
          @additionalOptions={{array AllGradesOption}}
          @gradePositions={{this.gradePositions}}
          @gradePosition={{this.currentGradePosition}}
          @updateGradePosition={{this.selectGradePosition}}
          @themeKey={{themeKey this.subscriptionType.current}}
        />
      </div>
      <div class="mb-4 px-3 md:w-full lg:w-1/2">
        <label id="{{this.componentId}}-assignment-lessons-label" class="mb-1">
          {{t "assignments.create.activityLesson"}}
        </label>
        <BaseSelect
          data-test-select-lesson
          class="w-full"
          name="activity-lesson"
          value={{or @selectedAssignmentOption.id ""}}
          aria-labelledby="{{this.componentId}}-assignment-lessons-label"
          @themeKey={{themeKey this.subscriptionType.current}}
          {{on "input" (eventValue this.selectOption)}}
        >
          <option value="">{{t "assignments.create.activityLessonPlaceholder"}}</option>
          {{#each this.stageGroupKeys as |stage|}}
            <optgroup label={{t (concat "assignments.create.spellingLessonStages." stage)}}>
              {{#each (get this.optionsGroupedByStage stage) as |optionItem|}}
                <option value={{optionItem.id}} selected={{eq optionItem.id @selectedAssignmentOption.id}}>
                  {{optionItem.label}}
                </option>
              {{/each}}
            </optgroup>
          {{/each}}
        </BaseSelect>
      </div>
    </div>
  </template>
}
